import { api } from "@/lib/api";
import { API } from "@/config";

export type MontageRenderStatus = "IN_ATTESA" | "RENDERING" | "COMPLETATO" | "ERRORE";
export type MontageAspectRatio = "9:16" | "16:9";

export interface MontageItem {
  id: string;
  source_type: "upload" | "clip";
  name: string;
  duration: number;
  effective_duration: number;
  width: number;
  height: number;
  has_audio: boolean;
  thumbnail_path?: string | null;
  in_point: number | null;
  out_point: number | null;
}

export interface MontageRender {
  id: string;
  montage_id: string;
  status: MontageRenderStatus;
  duration_seconds?: number | null;
  expected_duration?: number;
  error_message?: string | null;
  created_at: string;
  render_phase?: "ENCODING" | "STORING" | "COMPLETED" | null;
  progress_pct?: number | null;
  progress_out_time_s?: number | null;
  progress_speed_x?: number | null;
  eta_seconds?: number | null;
}

export type WatermarkPosition = "top-left" | "top-right" | "bottom-left" | "bottom-right" | "center";

export interface MontageWatermark {
  id: string;
  filename: string;
  content_type: string;
  width: number;
  height: number;
  position: WatermarkPosition;
  size: number;
  opacity: number;
  margin: number;
  has_file: boolean;
  updated_at: string;
}

export type TextPosition = "top" | "center" | "bottom";
export type TextSize = "small" | "medium" | "large";

export interface MontageText {
  id: string;
  text: string;
  position: TextPosition;
  size: TextSize;
  start: number;
  end: number;
  updated_at: string;
}

export type MontageTextInput = Pick<MontageText, "text" | "position" | "size" | "start" | "end">;

export interface MontageMusic {
  filename: string;
  content_type: string;
  duration: number;
  volume: number;
  original_volume: number;
  fade_in: number;
  fade_out: number;
  loop: boolean;
  has_file: boolean;
  updated_at: string;
}

export interface Montage {
  id: string;
  title: string;
  aspect_ratio: MontageAspectRatio;
  watermark: MontageWatermark | null;
  watermarks: MontageWatermark[];
  music: MontageMusic | null;
  texts: MontageText[];
  items: MontageItem[];
  total_duration: number;
  active_render_id?: string | null;
  render: MontageRender | null;
  updated_at: string;
}

export interface MontageSummary {
  id: string;
  title: string;
  aspect_ratio: MontageAspectRatio;
  item_count: number;
  total_duration: number;
  updated_at: string;
}

export interface LibraryClip {
  id: string;
  project_title?: string | null;
  scene_title?: string | null;
  duration?: number | null;
}

export const listMontages = () => api.get<MontageSummary[]>("/montage").then((r) => r.data);
export const createMontage = (title: string) =>
  api.post<Montage>("/montage", { title }).then((r) => r.data);
export const getMontage = (id: string) => api.get<Montage>(`/montage/${id}`).then((r) => r.data);
export const deleteMontage = (id: string) => api.delete(`/montage/${id}`).then((r) => r.data);
export const updateMontage = (id: string, patch: { title?: string; aspect_ratio?: MontageAspectRatio }) =>
  api.patch<Montage>(`/montage/${id}`, patch).then((r) => r.data);
export const libraryClips = () => api.get<LibraryClip[]>("/montage/library/clips").then((r) => r.data);
export const addFromClip = (id: string, clipId: string) =>
  api.post<Montage>(`/montage/${id}/items/from-clip`, { clip_id: clipId }).then((r) => r.data);
export const trimItem = (id: string, itemId: string, inPoint: number | null, outPoint: number | null) =>
  api.patch<Montage>(`/montage/${id}/items/${itemId}`, { in_point: inPoint, out_point: outPoint }).then((r) => r.data);
export const removeItem = (id: string, itemId: string) =>
  api.delete<Montage>(`/montage/${id}/items/${itemId}`).then((r) => r.data);
export const reorderItems = (id: string, itemIds: string[]) =>
  api.put<Montage>(`/montage/${id}/order`, { item_ids: itemIds }).then((r) => r.data);
export const startRender = (id: string) => api.post<MontageRender>(`/montage/${id}/render`).then((r) => r.data);
export const getRender = (id: string) => api.get<MontageRender | null>(`/montage/${id}/render`).then((r) => r.data);
export const thumbnailUrl = (id: string, itemId: string) => `${API}/montage/${id}/items/${itemId}/thumbnail`;

const upload = (url: string, file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post<Montage>(url, fd).then((r) => r.data);
};

export const uploadWatermark = (id: string, file: File) => upload(`/montage/${id}/watermark`, file);
export const patchWatermark = (
  id: string,
  patch: Partial<Pick<MontageWatermark, "position" | "size" | "opacity" | "margin">>,
) => api.patch<Montage>(`/montage/${id}/watermark`, patch).then((r) => r.data);
export const deleteWatermark = (id: string) => api.delete<Montage>(`/montage/${id}/watermark`).then((r) => r.data);
export const addWatermark = (id: string, file: File) => upload(`/montage/${id}/watermarks`, file);
export const patchWatermarkById = (
  id: string,
  wmId: string,
  patch: Partial<Pick<MontageWatermark, "position" | "size" | "opacity" | "margin">>,
) => api.patch<Montage>(`/montage/${id}/watermarks/${wmId}`, patch).then((r) => r.data);
export const deleteWatermarkById = (id: string, wmId: string) =>
  api.delete<Montage>(`/montage/${id}/watermarks/${wmId}`).then((r) => r.data);
export const watermarkImageUrlById = (id: string, wmId: string, v?: string) =>
  `${API}/montage/${id}/watermarks/${wmId}/image?v=${encodeURIComponent(v || "")}`;
export const watermarkImageUrl = (id: string, v?: string) =>
  `${API}/montage/${id}/watermark/image?v=${encodeURIComponent(v || "")}`;

export const uploadMusic = (id: string, file: File) => upload(`/montage/${id}/music`, file);
export const addText = (id: string, body: MontageTextInput) =>
  api.post<Montage>(`/montage/${id}/texts`, body).then((r) => r.data);
export const patchText = (id: string, textId: string, body: Partial<MontageTextInput>) =>
  api.patch<Montage>(`/montage/${id}/texts/${textId}`, body).then((r) => r.data);
export const deleteText = (id: string, textId: string) =>
  api.delete<Montage>(`/montage/${id}/texts/${textId}`).then((r) => r.data);
export const patchMusic = (
  id: string,
  patch: Partial<Pick<MontageMusic, "volume" | "original_volume" | "fade_in" | "fade_out" | "loop">>,
) => api.patch<Montage>(`/montage/${id}/music`, patch).then((r) => r.data);
export const deleteMusic = (id: string) => api.delete<Montage>(`/montage/${id}/music`).then((r) => r.data);
export const musicAudioUrl = (id: string, v?: string) =>
  `${API}/montage/${id}/music/audio?v=${encodeURIComponent(v || "")}`;

export const renderVideoUrl = (renderId: string) => `${API}/montage/renders/${renderId}/video`;
export const renderDownloadUrl = (renderId: string) => `${API}/montage/renders/${renderId}/video?download=true`;

const CHUNK = 4 * 1024 * 1024;
const CHUNK_RETRIES = 3;
const POLL_MS = 2000;
export const UPLOAD_PROCESSING_CLIENT_TIMEOUT_MS = 20 * 60 * 1000;

export type UploadStatus = "UPLOADING" | "PROCESSING" | "READY" | "ERRORE";
export interface UploadState {
  upload_id: string;
  status: UploadStatus;
  phase: "queued" | "assembling" | "storing" | null;
  error: string | null;
  item_id: string | null;
}
export type UploadProgress = { pct: number; phase?: UploadState["phase"] };

export class UploadError extends Error {
  constructor(message: string, readonly detail?: string) {
    super(message);
  }
}
/** Backend still processing after the client wait budget: the job is NOT failed, only the wait is over. */
export class UploadStillProcessing extends Error {
  constructor(readonly uploadId: string) {
    super("still processing");
  }
}

export const getUploadLimits = () => api.get<{ max_upload_bytes: number }>("/montage/limits").then((r) => r.data);
export const getUploadState = (id: string, uploadId: string) =>
  api.get<UploadState>(`/montage/${id}/uploads/${uploadId}`).then((r) => r.data);

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const putChunk = async (url: string, blob: Blob) => {
  // Idempotent on the server (same index → same slot): safe to retransmit after a transient failure.
  for (let attempt = 0; ; attempt++) {
    try {
      await api.put(url, blob, { headers: { "Content-Type": "application/octet-stream" } });
      return;
    } catch (err: any) {
      const status = err?.response?.status;
      if (attempt >= CHUNK_RETRIES || (status && status < 500 && status !== 408 && status !== 429)) throw err;
      await sleep(1000 * 2 ** attempt);
    }
  }
};

export async function uploadMontageVideo(
  id: string,
  file: File,
  onProgress: (p: UploadProgress) => void,
): Promise<Montage> {
  const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK));
  const { data } = await api.post<{ upload_id: string }>(`/montage/${id}/uploads/init`, {
    filename: file.name,
    total_chunks: totalChunks,
    size: file.size,
  });
  for (let i = 0; i < totalChunks; i++) {
    const blob = file.slice(i * CHUNK, Math.min(file.size, (i + 1) * CHUNK));
    await putChunk(`/montage/${id}/uploads/${data.upload_id}/chunk/${i}`, blob);
    onProgress({ pct: Math.round(((i + 1) / totalChunks) * 100) });
  }
  let state = (await api.post<UploadState>(`/montage/${id}/uploads/${data.upload_id}/complete`)).data;
  const deadline = Date.now() + UPLOAD_PROCESSING_CLIENT_TIMEOUT_MS;
  while (state.status === "PROCESSING" || state.status === "UPLOADING") {
    onProgress({ pct: 100, phase: state.phase ?? "queued" });
    if (Date.now() > deadline) throw new UploadStillProcessing(data.upload_id);
    await sleep(POLL_MS);
    state = await getUploadState(id, data.upload_id);
  }
  if (state.status === "ERRORE") throw new UploadError(state.error || "Caricamento non riuscito", state.error || undefined);
  return getMontage(id);
}

export const fmtDuration = (s: number | null | undefined) => {
  const t = Math.max(0, Math.round((s || 0) * 10) / 10);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = (t % 60).toFixed(1).padStart(4, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
};
